import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router';
import invariant from 'tiny-invariant';

// Utils
import { PATHS } from '~fpu/config/constants';

// Types
import { QuizId } from '~fpu/modules/MemberDashboard/types';
import { LessonNumber } from '~fpu/types';

export type QuizResult = {
  displayOrder: number;
  options: { text: string; count: number }[];
  question: string;
};

interface QuizResultsResponse {
  results: QuizResult[];
}

function fetchQuizResults(classCode: string, quizCategory: QuizId) {
  const allowedQuizCategories = Object.values(LESSON_POLL_MAP);
  allowedQuizCategories.push('nerd-free-spirit-spender-saver-quiz');

  if (!allowedQuizCategories.includes(quizCategory)) {
    throw new Error('Invalid quiz category');
  }

  const url = `${PATHS.API.QUIZ}/${classCode}/${quizCategory}`;
  return axios.get(url);
}

function useQuizResults(quizCategory: QuizId) {
  const { classId } = useParams<{ classId: string }>();
  invariant(classId, 'classId is required to fetch quiz results');

  const [searchParams] = useSearchParams();
  const selectedTab = searchParams.get('tab');

  const TWO_SECONDS = 2000;

  return useQuery({
    queryKey: ['quiz-results', classId, quizCategory],
    queryFn: () => fetchQuizResults(classId, quizCategory),
    select: (res: { data: QuizResultsResponse }) => res.data.results,
    refetchInterval: selectedTab === 'class-day' ? TWO_SECONDS : false // only refetch when on class-day tab
  });
}

const WORKSHOP_ALLOWED_QUIZ_CATEGORIES: QuizId[] = [
  'nerd-free-spirit-spender-saver-quiz'
];

function fetchWorkshopQuizResults(workshopId: string, quizCategory: QuizId) {
  if (!WORKSHOP_ALLOWED_QUIZ_CATEGORIES.includes(quizCategory)) {
    throw new Error('Invalid quiz category');
  }

  const url = PATHS.API.WORKSHOPS.QUIZ(workshopId, quizCategory);
  return axios.get(url);
}

function useWorkshopQuizResults(workshopId: string, quizCategory: QuizId) {
  const [searchParams] = useSearchParams();
  const selectedTab = searchParams.get('tab');

  const TWO_SECONDS = 2000;

  return useQuery({
    queryKey: ['workshop-quiz-results', workshopId, quizCategory],
    queryFn: () => fetchWorkshopQuizResults(workshopId, quizCategory),
    select: (res: { data: QuizResultsResponse }) => res.data.results,
    refetchInterval: selectedTab === 'modules' ? TWO_SECONDS : false // only refetch when on modules tab
  });
}

const LESSON_POLL_MAP: Record<LessonNumber, QuizId> = {
  1: 'lesson-one-poll',
  2: 'lesson-two-poll',
  3: 'lesson-three-poll',
  4: 'lesson-four-poll',
  5: 'lesson-five-poll',
  6: 'lesson-six-poll',
  7: 'lesson-seven-poll',
  8: 'lesson-eight-poll',
  9: 'lesson-nine-poll'
};

export const getClassPollQuizCategory = (lessonNumber: LessonNumber) =>
  LESSON_POLL_MAP[lessonNumber];

export { useQuizResults, useWorkshopQuizResults };
